import './legal.css';
import { TERMS_OF_SERVICE, PRIVACY_POLICY, type LegalDocument } from './legal-content.js';

/**
 * CAOS-225 — the two legal pages the sign-in footer already links to (`sign-in.ts` has pointed
 * `/legal/terms` and `/legal/privacy` at these paths since before this ticket; nothing resolved
 * there until now).
 *
 * Deliberately NOT a `SurfaceRenderer` registered with `SurfaceHost`. That contract exists for the
 * kernel's own pre-install decision (`sign-in` / `empty-workspace` / `error` / `hand-to-shell`) —
 * see `host.ts`. A legal page is none of those: it must render the same way whether a session
 * exists, whether an org resolves, or whether the kernel can be reached at all, since it needs to
 * be readable from the sign-in screen by someone who is by definition not signed in yet. So it is
 * checked and rendered directly in `main.ts`, BEFORE the kernel boot call, rather than threaded
 * through a `SurfaceDecision`.
 *
 * Styled with the same `--caos-*` tokens every other surface uses (the design-system floor is
 * imported unconditionally at the top of `main.ts`, so the baked default tokens are already in
 * place with no kernel round-trip needed) — no inline `style=`, so the strict `style-src 'self'`
 * CSP holds here too.
 */

export const LEGAL_TERMS_PATH = '/legal/terms';
export const LEGAL_PRIVACY_PATH = '/legal/privacy';

/** Whether `pathname` is one of the two reserved legal routes. */
export function isLegalPath(pathname: string): boolean {
  return pathname === LEGAL_TERMS_PATH || pathname === LEGAL_PRIVACY_PATH;
}

function documentFor(pathname: string): LegalDocument {
  return pathname === LEGAL_PRIVACY_PATH ? PRIVACY_POLICY : TERMS_OF_SERVICE;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

/** Render one legal document (Terms or Privacy, chosen by `pathname`) as a standalone page. */
export function renderLegalSurface(pathname: string): HTMLElement {
  const doc = documentFor(pathname);
  const page = el('div', 'caos-legal');
  const article = el('article', 'caos-legal__card');

  const back = el('a', 'caos-legal__back');
  back.href = '/';
  back.textContent = '← Back to sign in';
  article.append(back);

  const h1 = el('h1', 'caos-legal__title');
  h1.textContent = doc.title;
  article.append(h1);

  const dateNote = el('p', 'caos-legal__date');
  dateNote.textContent = doc.effectiveDateNote;
  article.append(dateNote);

  const notice = el('p', 'caos-legal__notice');
  notice.textContent = doc.draftNotice;
  article.append(notice);

  // The other document, so a reader on one page can reach the other without going back first.
  const otherPath = pathname === LEGAL_PRIVACY_PATH ? LEGAL_TERMS_PATH : LEGAL_PRIVACY_PATH;
  const otherTitle = pathname === LEGAL_PRIVACY_PATH ? TERMS_OF_SERVICE.title : PRIVACY_POLICY.title;
  const switchLink = el('a', 'caos-legal__switch');
  switchLink.href = otherPath;
  switchLink.textContent = `Read our ${otherTitle} →`;
  article.append(switchLink);

  for (const block of doc.blocks) {
    const h2 = el('h2', 'caos-legal__heading');
    h2.textContent = block.heading;
    article.append(h2);
    for (const item of block.body) {
      if (Array.isArray(item)) {
        const ul = el('ul', 'caos-legal__list');
        for (const entry of item) {
          const li = el('li');
          li.textContent = entry;
          ul.append(li);
        }
        article.append(ul);
      } else {
        const p = el('p', 'caos-legal__paragraph');
        p.textContent = item as string;
        article.append(p);
      }
    }
  }

  page.append(article);
  return page;
}
